import { isDefined } from "@clipboard-health/util-ts";
import { type WorkerMissingLicenseBookabilityUnmetCriteriaStatus } from "@src/appV2/OpenShifts/ShiftAction/types";

import { type MissingCredentialType } from "../../Documents/types";
import { ShiftType } from "../constants";

export interface OpenShiftCardPillsInput {
  shiftType: ShiftType;
  blockingRequirementsPillContext?: {
    blockingRequirementsPillCount: number;
    showBlockingRequirementsPill: boolean;
    areBlockingRequirementsExpiringSoon: boolean;
    blockingRequirementExpiryDate?: Date;
  };
  unbookableWindowName?: string;
  unbookableWindowThreshold?: number;
  workerScore?: number;
  licensePillStatus?: WorkerMissingLicenseBookabilityUnmetCriteriaStatus;
  basketSizeLimit?: number;
  leadTimeThreshold?: number;
  hasPaymentsDisabled: boolean;
  hasAgreementToSign: boolean;
  hasDemoShiftToComplete: boolean;
  hasShiftAuction: boolean;
  isOrientationRequired: boolean;
  missingCredentialType: MissingCredentialType | null;
}

export interface OpenShiftCardPillsProps {
  showUnbookableWindowPill: boolean;
  showBasketSizeLimitPill: boolean;
  showPriorityPill: boolean;
  showPriorityPlusPill: boolean;
  showLicensePill: boolean;
  showBlockingRequirementsPill: boolean;
  showShiftAuctionPill: boolean;
  pillsCount: number;
  hasMultiplePills: boolean;
  shouldRenderPills: boolean;
  shouldShowWindowedOutPillOnly: boolean;
  showAgreementToSignPill: boolean;
  showPaymentsDisabledPill: boolean;
  showDemoShiftPill: boolean;
  unbookableWindowName?: string;
  unbookableWindowThreshold?: number;
  workerScore?: number;
  licensePillStatus?: WorkerMissingLicenseBookabilityUnmetCriteriaStatus;
  blockingRequirementsPillContext?: {
    blockingRequirementsPillCount: number;
    showBlockingRequirementsPill: boolean;
    areBlockingRequirementsExpiringSoon: boolean;
    blockingRequirementExpiryDate?: Date;
  };
  showOrientationRequiredPill: boolean;
  showWorkplaceCredentialRequirementsPill: boolean;
  showBaseCredentialRequirementsPill: boolean;
}

export function getOpenShiftCardPillsProps(
  input: OpenShiftCardPillsInput
): OpenShiftCardPillsProps {
  const {
    shiftType,
    blockingRequirementsPillContext,
    unbookableWindowName,
    unbookableWindowThreshold,
    basketSizeLimit,
    leadTimeThreshold,
    licensePillStatus,
    workerScore,
    hasPaymentsDisabled,
    hasAgreementToSign,
    hasDemoShiftToComplete,
    hasShiftAuction,
    isOrientationRequired,
    missingCredentialType,
  } = input;

  const showUnbookableWindowPill = isDefined(unbookableWindowName);
  const showBasketSizeLimitPill = isDefined(basketSizeLimit) || isDefined(leadTimeThreshold);
  const showShiftAuctionPill = hasShiftAuction;
  const showPriorityPill =
    shiftType === ShiftType.PRIORITY || shiftType === ShiftType.PRIORITY_LEGACY;
  const showPriorityPlusPill = shiftType === ShiftType.PRIORITY_PLUS;
  const showLicensePill = isDefined(licensePillStatus);

  const showDemoShiftPill = hasDemoShiftToComplete;

  const showOrientationRequiredPill = isOrientationRequired;

  const showWorkplaceCredentialRequirementsPill =
    missingCredentialType === "WORKPLACE_CREDENTIAL" && !showLicensePill;

  const showBaseCredentialRequirementsPill =
    missingCredentialType === "BASE_CREDENTIAL" && !showLicensePill;

  // Don't show blocking requirements pill if license or demo shift pill is already shown
  // Don't show blocking requirements pill if workplace or base credential requirements pills are shown
  const { showBlockingRequirementsPill: originalShowBlockingRequirementsPill = false } =
    blockingRequirementsPillContext ?? {};
  const showBlockingRequirementsPill =
    originalShowBlockingRequirementsPill &&
    !showLicensePill &&
    !showDemoShiftPill &&
    !showWorkplaceCredentialRequirementsPill &&
    !showBaseCredentialRequirementsPill;

  // Payments and agreement are included in requirements pill if there are blocking requirements
  const showPaymentsDisabledPill = !showBlockingRequirementsPill && hasPaymentsDisabled;
  const showAgreementToSignPill = !showBlockingRequirementsPill && hasAgreementToSign;

  const pillsCount = [
    showBasketSizeLimitPill,
    showShiftAuctionPill,
    showPriorityPill,
    showPriorityPlusPill,
    showBlockingRequirementsPill,
    showUnbookableWindowPill,
    showLicensePill,
    showDemoShiftPill,
    showPaymentsDisabledPill,
    showAgreementToSignPill,
    showOrientationRequiredPill,
    showWorkplaceCredentialRequirementsPill,
    showBaseCredentialRequirementsPill,
  ].filter(Boolean).length;

  const hasMultiplePills = pillsCount > 1;
  const shouldRenderPills = pillsCount > 0;
  const shouldShowWindowedOutPillOnly = showUnbookableWindowPill;

  return {
    showUnbookableWindowPill,
    showBasketSizeLimitPill,
    showShiftAuctionPill,
    showPriorityPill,
    showPriorityPlusPill,
    showLicensePill,
    showBlockingRequirementsPill,
    pillsCount,
    hasMultiplePills,
    shouldRenderPills,
    shouldShowWindowedOutPillOnly,
    showAgreementToSignPill,
    showPaymentsDisabledPill,
    showDemoShiftPill,
    unbookableWindowName,
    unbookableWindowThreshold,
    workerScore,
    licensePillStatus,
    blockingRequirementsPillContext,
    showOrientationRequiredPill,
    showWorkplaceCredentialRequirementsPill,
    showBaseCredentialRequirementsPill,
  };
}
